import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showItemExistsError } from '../../../../core/alert/sweetAlertConfig';

interface FormErrors {
  vehiclePartName: string;
  vehiclePartNo: string;
  selectedVehiclePart: string;
}

interface VehiclePart {
  id: number;
  name: string;
  partNo: string;
  createdBy?: string;
  createdAt?: string;
}

interface VehiclePartData {
  status: string;
  data: {
    vehicleParts: VehiclePart[];
    total: number;
  };
  statusDescription?: string;
}

const AddVehiclePart: React.FC = () => {
  const [vehiclePartData, setVehiclePartData] = useState<VehiclePartData>({ status: "", data: { vehicleParts: [], total: 0 } });
  const [isVehiclePartLoading, setIsVehiclePartLoading] = useState<boolean>(true);
  const [selectedVehiclePartId, setSelectedVehiclePartId] = useState<string>("");
  const [newVehiclePartName, setNewVehiclePartName] = useState<string>("");
  const [newVehiclePartNo, setNewVehiclePartNo] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrors>({
    vehiclePartName: "",
    vehiclePartNo: "",
    selectedVehiclePart: "",
  });
  const [openFail, setOpenFail] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Operation Failed");

  const handleCloseFail = () => setOpenFail(false);

  // Fetch vehicle parts on component mount
  useEffect(() => {
    fetchVehicleParts();
  }, []);

  const fetchVehicleParts = () => {
    setIsVehiclePartLoading(true);
    const payload = {
      filterType: "",
      filterValue: "",
      date: ""
    };
    apiDefinitions.getVehicleParts(1, payload)
      .then((response: { data: VehiclePartData }) => {
        if (response.data.status === "S1000" && Array.isArray(response.data.data.vehicleParts)) {
          setVehiclePartData({
            ...response.data,
            data: {
              vehicleParts: response.data.data.vehicleParts || [],
              total: response.data.data.total || 0
            }
          });
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load vehicle parts");
          setOpenFail(true);
          setVehiclePartData({ status: response.data.status, data: { vehicleParts: [], total: 0 } });
        }
        setIsVehiclePartLoading(false);
      })
      .catch((error: any) => {
        setErrorMessage(error.response?.data?.statusDescription || "Failed to load vehicle parts: Server error");
        setOpenFail(true);
        setVehiclePartData({ status: "", data: { vehicleParts: [], total: 0 } });
        setIsVehiclePartLoading(false);
      });
  };

  const validateVehiclePartForm = (name: string, partNo: string): boolean => {
    const errors: FormErrors = { vehiclePartName: "", vehiclePartNo: "", selectedVehiclePart: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.vehiclePartName = "Vehicle part name is required";
      isValid = false;
    }
    if (!partNo.trim()) {
      errors.vehiclePartNo = "Vehicle part number is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedVehiclePart = (): boolean => {
    const errors: FormErrors = { vehiclePartName: "", vehiclePartNo: "", selectedVehiclePart: "" };
    let isValid = true;

    if (!selectedVehiclePartId) {
      errors.selectedVehiclePart = "Please select a vehicle part";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddVehiclePartSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateVehiclePartForm(newVehiclePartName, newVehiclePartNo)) return;

    apiDefinitions.addVehiclePart({ name: newVehiclePartName, partNo: newVehiclePartNo })
      .then((response: { data: VehiclePartData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Vehicle part added successfully");
          setNewVehiclePartName("");
          setNewVehiclePartNo("");
          fetchVehicleParts();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle part with this name already exists.");
        } else {
          setErrorMessage(response.data.statusDescription || "Vehicle part creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle part with this name already exists.");
        } else {
          setErrorMessage(error.response?.data?.statusDescription || "Failed to create vehicle part: Server error");
          setOpenFail(true);
        }
      });
  };

//   const handleDeleteVehiclePart = () => {
//     if (!validateSelectedVehiclePart()) return;

//     showDeleteConfirmation(() => {
//       apiDefinitions.deleteVehiclePart(parseInt(selectedVehiclePartId))
//         .then((response: { data: VehiclePartData }) => {
//           if (response.data.status === "S1000") {
//             setSelectedVehiclePartId("");
//             fetchVehicleParts();
//           } else if (response.data.statusDescription?.toLowerCase().includes("already used")) {
//             showItemAlreadyUsed();
//           } else {
//             showItemAlreadyUsed();
//           }
//         })
//         .catch((error: any) => {
//           if (error.response?.data?.statusDescription?.toLowerCase().includes("already used")) {
//             showItemAlreadyUsed();
//           } else {
//             showItemAlreadyUsed();
//           }
//         });
//     });
//   };

  const handleVehiclePartChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehiclePartId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedVehiclePart: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Vehicle Part</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Vehicle Part</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newVehiclePartName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehiclePartName(e.target.value)}
                        placeholder="Enter vehicle part name (e.g., Engine Oil Filter)"
                      />
                      <input
                        type="text"
                        className="form-control ms-3"
                        style={{ width: '150px', padding: '10px 15px' }}
                        value={newVehiclePartNo}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehiclePartNo(e.target.value)}
                        placeholder="Enter part number (e.g., EOF123)"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#vehicle-part-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Part
                      </button>
                    </div>
                    {formErrors.vehiclePartName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehiclePartName}</div>
                    )}
                    {formErrors.vehiclePartNo && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehiclePartNo}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedVehiclePartId}
                      onChange={handleVehiclePartChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Vehicle Part</option>
                      {isVehiclePartLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        Array.isArray(vehiclePartData.data.vehicleParts) && vehiclePartData.data.vehicleParts.length > 0 ? (
                          vehiclePartData.data.vehicleParts.map((vehiclePart: VehiclePart) => (
                            <option key={vehiclePart.id} value={vehiclePart.id}>{`${vehiclePart.name} (${vehiclePart.partNo})`}</option>
                          ))
                        ) : (
                          <option disabled>No vehicle parts available</option>
                        )
                      )}
                    </select>
                    {formErrors.selectedVehiclePart && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedVehiclePart}</div>
                    )}
                    {/* <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteVehiclePart}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vehicle Part Modal */}
      <div className="modal fade" id="vehicle-part-modal" tabIndex={-1} aria-labelledby="add-vehicle-part" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-vehicle-part">Add New Vehicle Part</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVehiclePartSubmit}>
                <div className="mb-3">
                  <label htmlFor="newVehiclePartName" className="form-label">Vehicle Part Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehiclePartName"
                    value={newVehiclePartName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehiclePartName(e.target.value)}
                    placeholder="Enter vehicle part name (e.g., Engine Oil Filter)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehiclePartName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehiclePartName}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label htmlFor="newVehiclePartNo" className="form-label">Vehicle Part Number</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehiclePartNo"
                    value={newVehiclePartNo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehiclePartNo(e.target.value)}
                    placeholder="Enter part number (e.g., EOF123)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehiclePartNo && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehiclePartNo}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Vehicle Part
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Error Notification */}
      {openFail && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseFail}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );
};

export default AddVehiclePart;